import React, { createContext, useContext, useState, ReactNode } from 'react';

interface RespostaData {
    id: number;
    resposta: string;
}

interface Resposta {
    [fieldId: string]: RespostaData;
}

interface RespostaStore {
    [slug: string]: Resposta;
}

interface LastRespostaContextType {
    respostaStore: RespostaStore;
    setResposta: (slug: string, data: Resposta) => void;
    getResposta: (slug: string) => Resposta | null;
    clearResposta: (slug: string) => void;
}

const LastRespostaContext = createContext<LastRespostaContextType | undefined>(undefined);

export const LastRespostaProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [respostaStore, setRespostaStore] = useState<RespostaStore>({});

    const setResposta = (slug: string, data: Resposta) => {
        setRespostaStore(prevStore => {
            const existingData = prevStore[slug] || {};
            const mergedData = { ...existingData, ...data };
            return {
                ...prevStore,
                [slug]: mergedData,
            };
        });
    };

    const getResposta = (slug: string): Resposta | null => {
        return respostaStore[slug] || null;
    };

    const clearResposta = (slug: string) => {
        setRespostaStore(prevStore => {
            const newStore = { ...prevStore };
            delete newStore[slug];
            return newStore;
        });
    };

    const value: LastRespostaContextType = {
        respostaStore,
        setResposta,
        getResposta,
        clearResposta,
    };

    return (
        <LastRespostaContext.Provider value={value}>
            {children}
        </LastRespostaContext.Provider>
    );
};

export const useLastResposta = (): LastRespostaContextType => {
    const context = useContext(LastRespostaContext);
    if (context === undefined) {
        throw new Error('useLastResposta must be used within a LastRespostaProvider');
    }
    return context;
};